import { html } from '../lib/html.js';
import { openPhoto, toastError } from '../lib/ui.js';
import { authorizations, fmtDateTime } from '../services/authorizations.js';
import { maintenances, setMeterUnit } from '../services/maintenances.js';
import { Page, go } from './base.js';
import { history, pill, summary } from './maintenance-ui.js';
import { vehicleHero } from './vehicle-hero.js';

/**
 * /oficina/veiculos/:vehicleId — veículo autorizado, visto pela oficina:
 * km atual, próximas revisões, histórico completo (todas as oficinas) e registrar/corrigir.
 */
class WorkshopVehiclePage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/oficina/veiculos" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Veículo autorizado</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding surface-page" fullscreen>
        <ion-refresher slot="fixed"><ion-refresher-content></ion-refresher-content></ion-refresher>
        <div class="narrow body"></div>
      </ion-content>`;
  }

  setup() {
    this.$('ion-refresher').addEventListener('ionRefresh', async (e) => {
      await this.load();
      e.target.complete();
    });
    this.$('.body').addEventListener('click', (e) => {
      const goEl = e.target.closest('[data-go]');
      if (goEl) return go(goEl.dataset.go);
      const el = e.target.closest('[data-action]');
      if (!el) return;
      if (el.dataset.action === 'home') return go('/home', 'root');
      if (el.dataset.action === 'photo') return openPhoto(el.dataset.url);
      if (el.dataset.action === 'toggle') {
        this.open = this.open === el.dataset.id ? null : el.dataset.id;
        return this.render();
      }
      if (el.dataset.action === 'versions') return this.loadVersions(el.dataset.id);
    });
  }

  get vid() {
    if (this.vehicleId) return String(this.vehicleId);
    return decodeURIComponent(location.pathname.match(/\/veiculos\/([^/?#]+)/)?.[1] ?? '');
  }

  onEnter() {
    this.open = null;
    this.versions = {};
    this.data = null;
    this.load();
  }

  async load() {
    if (!this.data) this.$('.body').innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      const [v, m] = await Promise.all([authorizations.workshopVehicle(this.vid), maintenances.workshopList(this.vid)]);
      setMeterUnit(v.vehicle?.meterUnit ?? m.meterUnit);
      this.data = { ...v, maint: m };
      this.render();
    } catch (err) {
      this.$('.body').innerHTML = html`
        <div class="center">
          <ion-icon name="alert-circle-outline" class="empty-icon"></ion-icon>
          <h2>Sem acesso</h2>
          <p class="hint">${err.status === 404 ? 'A autorização deste veículo terminou ou foi revogada pelo dono.' : err.message}</p>
          <ion-button fill="outline" data-action="home">Voltar ao início</ion-button>
        </div>`.value;
    }
  }

  async loadVersions(id) {
    try {
      this.versions[id] = (await maintenances.workshopVersions(this.vid, id)).items;
      this.render();
    } catch (err) {
      toastError(err.message);
    }
  }

  render() {
    const { vehicle: v, authorization: a, maint } = this.data;
    this.$('.body').innerHTML = html`
      ${vehicleHero({
        plate: v.plateFormatted ?? v.plate,
        serial: v.serialNumber,
        category: v.category,
        title: [v.brand, v.model].filter(Boolean).join(' ') || 'Veículo',
        subtitle: [v.modelYear, v.color].filter(Boolean).join(' · '),
        status: pill('success', 'Autorizado'),
        extra: html`<p class="hero-note"><ion-icon name="time-outline"></ion-icon>Acesso até ${fmtDateTime(a.expiresAt)}</p>`,
      })}
      <ion-button expand="block" class="primary-cta biz" data-go="/oficina/veiculos/${v.id}/manutencao"><ion-icon slot="start" name="add-circle-outline"></ion-icon>Registrar serviço</ion-button>
      ${summary(maint)}
      ${history(maint.items, {
        initial: maint.initialData ?? [],
        open: this.open,
        versions: this.versions,
        correctHref: (m) => (m.canCorrect ? `/oficina/veiculos/${v.id}/manutencao/${m.id}` : null),
        emptyText: 'Este veículo ainda não tem serviços registrados. Use "Registrar manutenção" para lançar o primeiro.',
      })}`.value;
  }
}

customElements.define('page-workshop-vehicle', WorkshopVehiclePage);
